import React, { useState, useRef, useEffect, useMemo, useContext } from 'react';
import { useId } from '@fluentui/react-hooks';
import { DefaultButton, PrimaryButton, IStyleFunctionOrObject, IIconStyleProps, IIconStyles, keyframes, Callout, NeutralColors, Link, FontSizes } from '@fluentui/react';
import { useNavigate } from 'react-router-dom';
import { Wizard, StepStatus } from '@business-app/fabric/lib/components/Wizard';

import { About } from './steps/About';
import { scrollToTop } from 'utils/browser';
import { ApiError } from 'services/restService';
import { CheckResults } from './steps/CheckResults';
import { Configuration } from './steps/Configuration';
import { Announce } from 'components/common/Announce';
import { WizardTitle } from 'components/common/WizardTitle';
import { GatherInformation } from './steps/GatherInformation';
import { EnvironmentCheckResponse, useResultFile } from 'hooks/api';
import { Step, StepBaseProps, BaseState, StepRef, ProcessInfo, ConfigurationInfo, style, wizardStyle, progressStyle, footerStyle } from 'components/common/Wizard';
import TranslationsContext from 'translations/TranslationsContext';
import translationsProvider from 'translations/translationsProvider';
import translationKeys from 'translationKeys';

// needed for navigation through the steps:
const initialSteps: Step[] = [
	{
		name: translationsProvider.staticTranslate(translationKeys.EnvironmentCheck.InitialSteps.AboutName),
		summary: [],
		hasError: false,
		ariaDescription: translationsProvider.staticTranslate(translationKeys.EnvironmentCheck.InitialSteps.AboutDescription)
	},
	{
		name: translationsProvider.staticTranslate(translationKeys.EnvironmentCheck.InitialSteps.ConfirmSettingsName),
		summary: [],
		hasError: false,
		ariaDescription: translationsProvider.staticTranslate(translationKeys.EnvironmentCheck.InitialSteps.ConfirmSettingsDescription)
	},
	{
		name: translationsProvider.staticTranslate(translationKeys.EnvironmentCheck.InitialSteps.GatherSettingsName),
		summary: [],
		hasError: false,
		ariaDescription: translationsProvider.staticTranslate(translationKeys.EnvironmentCheck.InitialSteps.GatherSettingsDescription)
	},
	{
		name: translationsProvider.staticTranslate(translationKeys.EnvironmentCheck.InitialSteps.StatusReportName),
		summary: [],
		hasError: false,
		ariaDescription: translationsProvider.staticTranslate(translationKeys.EnvironmentCheck.InitialSteps.StatusReportDescription)
	}
];

const initialState: IState = {
	steps: initialSteps,
	stepIndex: 0,
	error: undefined,
	canGoNext: false,
	accNotification: undefined,
	environmentCheck: {
		id: undefined,
		url: undefined,
		organizationId: undefined,
		subscriptionId: undefined,
		resourceGroupId: undefined,
	},
	environmentCheckResult: null
};

export interface IEnvironmentCheck extends ProcessInfo, ConfigurationInfo { }

export interface StepProps extends StepBaseProps {
	environmentCheckResult: EnvironmentCheckResponse,
	environmentCheck: IEnvironmentCheck,
	stepParams?: StepParams
}

interface StepParams {
	inStepError?: boolean;
}

export type EnvironmentCheckComponent = (props: StepProps) => JSX.Element;

export interface IEnvironmentCheckInfo extends ProcessInfo, ConfigurationInfo {
	environmentCheckResult: EnvironmentCheckResponse
 }

export interface IState extends BaseState {
	environmentCheckResult: EnvironmentCheckResponse,
	environmentCheck: {
		id: string,
		url: string,
		organizationId: string,
		subscriptionId: string,
		resourceGroupId: string,
	},
}

const stepsComponentList: EnvironmentCheckComponent[] = [
	(props) => <About {...props} />,
	(props) => <Configuration {...props} />,
	(props) => <GatherInformation  {...props} />,
	(props) => <CheckResults {...props} />
];

type EnvironmentCheckStepComponent = (props: StepProps) => JSX.Element;

export const EnvironmentCheck = () => {
	const { t } = useContext(TranslationsContext)!;
	const exportBtnId: string = useId('exportButton');
	const exportBtnDesc: string = useId('exportButtonDesc');

	const navigate = useNavigate();
	const [state, setState] = useState<IState>({ ...initialState });

	const { steps, stepIndex, canGoNext, environmentCheck, error, accNotification, environmentCheckResult } = state;
	const isLastStep = stepIndex === steps.length - 1;

	const [resultFile] = useResultFile(environmentCheckResult?.result, environmentCheck?.resourceGroupId, environmentCheck?.organizationId);
	useEffect(scrollToTop, [stepIndex, error]);

	useEffect(() => {
		if (!isLastStep) return;

		let message = null;

		if (resultFile.isLoading) {
			message = t(translationKeys.EnvironmentCheck.ExportButton.Loading);
		} else if (!!resultFile.url) {
			message = t(translationKeys.EnvironmentCheck.ExportButton.Ready);
		} else if (resultFile.error) {
			message = t(translationKeys.EnvironmentCheck.ExportButton.Error);
		}

		setState({
			...state,
			error: resultFile.error,
			accNotification: message
		});
	}, [stepIndex, resultFile, t]);

	const getStatuses = () => steps.map((s, index, list) => {
		if (s.hasError) {
			return StepStatus.Error;
		}

		if (stepIndex === list.length - 1) {
			return StepStatus.Valid;
		}

		if (index >= stepIndex) {
			return StepStatus.Empty;
		}

		return StepStatus.Valid;
	});

	const resetErrors = () => {
		const newSteps = steps.map((step) => {
			step.hasError = false;

			return step;
		});

		setState({ ...state, steps: newSteps, error: null });
	};

	const stepRef = useRef<StepRef>({
		validateStep: () => Promise.resolve(true),
		getDatafromStep: () => Promise.resolve({}),
	});

	const callEnvironmentCheckApi = async ({ organizationId, subscriptionId, resourceGroupId, environmentCheckResult }: IEnvironmentCheckInfo): Promise<IState> => {
		try {
			return Promise.resolve({
				...state,
				environmentCheck: {
					...environmentCheck,
					organizationId: organizationId,
					subscriptionId: subscriptionId,
					resourceGroupId: resourceGroupId,
					id: null,
					url: '/EnvironmentCheck'
				},
				environmentCheckResult: environmentCheckResult
			});
		} catch (e) {
			return Promise.reject(e);
		}
	};



	const getStepInitiator = (index: number): (info: IEnvironmentCheckInfo) => Promise<IState> => {
		const defaultInitiator = (info: IState) => Promise.resolve({ ...state });

		let toReturn =  [null, callEnvironmentCheckApi][index] || defaultInitiator;

		return toReturn;
	};

	const resetStepRef = () => {
		stepRef.current.validateStep = () => Promise.resolve(true);
		stepRef.current.getDatafromStep = () => Promise.resolve({});
	};

	const setStepError = (index: number, error?: ApiError) => {
		const stepsToAddError = [...steps];
		stepsToAddError[index].hasError = true;

		setState({ ...state, error, steps: stepsToAddError, accNotification: getStepErrorNotification(index) });
	};

	const goToStep = async (index: number) => {
		const isForward = index > stepIndex;
		const nextIndex = stepIndex + 1;
		resetErrors();

		if (isForward) {
			if (await stepRef.current.validateStep()) {
				const stepData = await stepRef.current.getDatafromStep();
				let newEnvironmentCheck = { ...environmentCheck, ...stepData };

				let stepInitiator = getStepInitiator(stepIndex);

				stepInitiator(newEnvironmentCheck)
					.then(async (state: IState) => {
						resetStepRef();
						setState({
							...state,
							stepIndex: nextIndex,
							accNotification: getStepChangeNotification(nextIndex),
							environmentCheckResult: newEnvironmentCheck.environmentCheckResult
						});
					}).catch((error) => {
						setState({
							...state,
							error,
							accNotification: getStepErrorNotification(index)
						});
						setStepError(stepIndex);
					});
			}
		} else {
			setState({
				...initialState,
				canGoNext: index > 0,
				stepIndex: index
			});
		}
	};

	const getStepRenderer = (): EnvironmentCheckStepComponent => {
		const step = stepsComponentList[stepIndex];
		const fallbackStep = (props: StepProps) => <h2>{t(translationKeys.EnvironmentCheck.Fallback)}</h2>;

		return step || fallbackStep;
	};

	const getStepName = (stepIndex: number): string => initialSteps[stepIndex]?.name;

	const getStepChangeNotification = (index: number) => t(translationKeys.EnvironmentCheck.Notifications.StepChanged, { stepName: getStepName(index), stepNumber: index + 1, totalStepsCount: initialSteps.length });

	const getStepErrorNotification = (index: number) => t(translationKeys.EnvironmentCheck.Notifications.StepError, { stepName: getStepName(index), stepNumber: index + 1, totalStepsCount: initialSteps.length });

	const getMainAriaLabel = (index: number) => t(translationKeys.EnvironmentCheck.Labels.MainArea, { stepName: getStepName(index), stepNumber: index + 1, totalStepsCount: initialSteps.length });

	const checkCanGoNext = (newCanGo: boolean) => {
		if (newCanGo !== canGoNext) {
			setState({ ...state, canGoNext: newCanGo });
		}
	};

	const goToNextStep = () => goToStep(stepIndex + 1);

	const goToPreviousStep = () => goToStep(0);

	const onRenderStep = () => {
		const stepRenderer = getStepRenderer();

		return (
			<main className={style.stepShell} aria-label={getMainAriaLabel(stepIndex)}>
				{
					stepRenderer({
						error,
						stepRef,
						environmentCheck,
						environmentCheckResult,
						goToNextStep,
						resetErrors,
						checkCanGoNext,
						setStepError: (error) => setStepError(stepIndex, error),
						stepParams: { inStepError: !!resultFile.error }
					})
				}
			</main>
		);
	};

	const cancel = () => {
		if (stepIndex === 0) navigate('/');

		const indexToGo = (stepIndex > 1) ? 1 : (stepIndex - 1);

		goToStep(indexToGo);
	};

	const canGoToPreviousStep = !!error || stepIndex === 1;
	const canGoToNextStep = (canGoNext && stepIndex < steps.length);
	const shouldDisplayBackButton = !isLastStep;
	const shouldHideContinueButton = isLastStep;

	const wizardDefaultButtons = useMemo(() => (
		<>
			{shouldDisplayBackButton && <DefaultButton onClick={goToPreviousStep} text={t(translationKeys.EnvironmentCheck.NavigationButton.Back)} allowDisabledFocus disabled={!canGoToPreviousStep} />}
			{!shouldHideContinueButton && <PrimaryButton onClick={goToNextStep} text={t(translationKeys.EnvironmentCheck.NavigationButton.Next)} allowDisabledFocus disabled={!canGoToNextStep}/>}
		</>
	), [canGoToPreviousStep, canGoToNextStep, shouldDisplayBackButton, shouldHideContinueButton]);

	const renderDownloadFileBtn = () => {
		const isError = !!resultFile.error;
		const isDisabled = (!resultFile.url || resultFile.isLoading || isError);

		if (!isLastStep) return;

		return <>
			<PrimaryButton
				id={exportBtnId}
				allowDisabledFocus
				href={resultFile.url}
				disabled={isDisabled}
				aria-describedby={exportBtnDesc}
				aria-label={t(translationKeys.EnvironmentCheck.ExportButton.AriaLabel)}
				text={isError ? t(translationKeys.EnvironmentCheck.ExportButton.Failed) : t(translationKeys.EnvironmentCheck.ExportButton.Export)}
				download='environment-check-result.zip'
				iconProps={ isError ? {iconName: 'Info'} : isDisabled ? { iconName: 'Sync', styles: getIconStyles} : { iconName: 'Download' }}
			/>
			{ isError &&
				<Callout target={`#${exportBtnId}`} style={callOutStyles} id={exportBtnDesc}>
					<div>
						{t(translationKeys.EnvironmentCheck.Callout.Issue.InfoText)} <Link underline={false} target="_blank" href="https://admin.powerplatform.microsoft.com/support/">{t(translationKeys.EnvironmentCheck.Callout.Issue.LinkText)}</Link>.
						{error?.traceId && <p>{t(translationKeys.EnvironmentCheck.Callout.Issue.CorrelationId)} {error.traceId}</p>}
					</div>
				</Callout>
			}
		</>;
	};

	const footerSecondaryActions = useMemo(() => {
		const shoulShowCancel = !isLastStep && (stepIndex < 1 || stepIndex > 2);

		return (
			<div>
				{
					isLastStep && (<>
						<DefaultButton aria-label={t(translationKeys.EnvironmentCheck.RestartButton.AriaLabel)} text={t(translationKeys.EnvironmentCheck.RestartButton.Text)} onClick={() => goToStep(1)} allowDisabledFocus />
						<DefaultButton aria-label={t(translationKeys.EnvironmentCheck.CloseButton.AriaLabel)} text={t(translationKeys.EnvironmentCheck.CloseButton.Text)} onClick={() => navigate('/')} allowDisabledFocus />
					</>)
				}
				{
					shoulShowCancel &&  <DefaultButton text={t(translationKeys.EnvironmentCheck.CancelButton.Text)} aria-label={t(translationKeys.EnvironmentCheck.CancelButton.AriaLabel)} onClick={cancel} allowDisabledFocus disabled={stepIndex === 2} />
				}
			</div>
		);
	}, [isLastStep, stepIndex]);

	const renderFooterContent = (buttonRender: () => React.ReactNode, summaryRender: () => React.ReactNode) => (
		<section
			style={footerSectionStyles}
			aria-label={t(translationKeys.EnvironmentCheck.Footer.AriaLabel)}
		>
			<div>
				{wizardDefaultButtons}
				{renderDownloadFileBtn()}
			</div>
			{footerSecondaryActions}
		</section>
	);

	return (
		<>
			<Announce message={accNotification} />
			<WizardTitle content={t(translationKeys.EnvironmentCheck.WizardTitle.Content)} />
			<Wizard
				styles={wizardStyle}
				progressProps={{
					currentIndex: stepIndex,
					stepNames: steps.map(s => s.name),
					stepStatuses: getStatuses(),
					stepSummaries: steps.map(s => s.summary),
					computeAriaDescription: index => steps[index].ariaDescription,
					navLabel: t(translationKeys.EnvironmentCheck.Wizard.NavLabel),
					expandButtonLabel: t(translationKeys.EnvironmentCheck.Wizard.ExpandButtonLabel),
					collapseButtonLabel: t(translationKeys.EnvironmentCheck.Wizard.CollapseButtonLabel),
					styles: progressStyle
				}}
				footerProps={{
					styles: footerStyle,
					canGoToNextStep,
					renderFooterContent,
					canGoToPreviousStep,
					shouldDisplayBackButton,
					shouldHideContinueButton,
					backButtonText: t(translationKeys.EnvironmentCheck.NavigationButton.Back),
					continueButtonText: t(translationKeys.EnvironmentCheck.NavigationButton.Next),
					goToPreviousStep,
					goToNextStep,
				}}
				onRenderStep={onRenderStep}
			/>
		</>
	);
};

const footerSectionStyles = {
	display: 'flex',
	justifyContent: 'space-between',
	flexGrow: 1
};

const spin = keyframes({
	from: { transform: 'rotate(0deg)' },
	to: { transform: 'rotate(360deg)' },
});

const getIconStyles: IStyleFunctionOrObject<IIconStyleProps, IIconStyles> = (props) => {
	return {
		root: [
			{
				animation: `${spin} 1s linear infinite`,
				display: 'inline-block',
			}
		],
	};
};

const callOutStyles = {
	display: 'flex',
	flexdDirection: 'column',
	padding: '1.25rem 1.5rem',
	gap: '0.75rem',
	background: NeutralColors.white,
	borderradius: '0.25rem',
	width: '343px',
	fontSize: FontSizes.size12,
	lineHeight: FontSizes.size16,
	alignSelf: 'stretch'
};
